'use client';
import React, { useState, useEffect } from 'react';
import { getLicenceState, activateLicence, LicenceState } from '@/lib/licenceUtils';
import { reconcileLicence, sourceFromEntitlements, type LicenceSource } from '@/lib/licence-server';
import { getMyEntitlements } from '@/lib/entitlements';
import Icon from '@/components/ui/AppIcon';

interface LicenceBannerProps {
  onActivated?: () => void;
}

// Persisted dismissal: only suppresses the demo-mode banner. The expired banner
// always shows again so users never miss that their demo has run out.
const DISMISS_KEY = 'diamond_crm_licence_banner_dismissed';

export default function LicenceBanner({ onActivated }: LicenceBannerProps) {
  const [licenceState, setLicenceState] = useState<LicenceState | null>(null);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [keyInput, setKeyInput] = useState('');
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState('');
  const [activateSuccess, setActivateSuccess] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Optimistic first paint from cache, then correct against the server.
    // The server (subscriptions + entitlements, RLS-scoped) is authoritative;
    // localStorage is only an offline fallback.
    const cached = getLicenceState();
    setLicenceState(cached);

    let cancelled = false;
    (async () => {
      let source: LicenceSource = { kind: 'unknown' };
      try {
        const ent = await getMyEntitlements();
        if (!cancelled) {
          source = sourceFromEntitlements({
            status: ent.snapshot.status,
            trialEndAt: ent.snapshot.trialEndAt,
            currentPeriodEnd: ent.snapshot.currentPeriodEnd,
            planSlug: ent.snapshot.planSlug,
          });
          setLicenceState(reconcileLicence(source, cached));
        }
      } catch {
        // Network/permission failure: keep the cached state.
      }
    })();

    try {
      if (cached.mode !== 'expired' && localStorage.getItem(DISMISS_KEY) === '1') {
        setDismissed(true);
      }
    } catch {
      // Storage unavailable — banner just re-shows on reload.
    }

    return () => {
      cancelled = true;
    };
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Ignore — dismissal just won't persist.
    }
  };

  if (!licenceState || licenceState.mode === 'activated' || dismissed) return null;

  const isExpired = licenceState.mode === 'expired';
  const isUrgent = !isExpired && licenceState.daysRemaining <= 2;

  const handleActivate = async () => {
    if (!keyInput.trim()) {
      setActivateError('Please enter a licence key');
      return;
    }
    setActivating(true);
    setActivateError('');
    try {
      // Activation goes through the server route — the browser never queries
      // licence_keys directly (that endpoint reveals nothing about other keys).
      const res = await fetch('/api/licence/activate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licence_key: keyInput.trim().toUpperCase() }),
      });
      const body = await res.json().catch(() => ({}));

      if (!res.ok || !body.activated) {
        setActivateError(
          body.error || 'Invalid or inactive licence key. Please check and try again.'
        );
        setActivating(false);
        return;
      }

      // Activate locally
      activateLicence(body.licence_key, body.licence_type as 'yearly' | 'lifetime');
      setActivateSuccess(true);
      setLicenceState(getLicenceState());
      setTimeout(() => {
        setShowActivateModal(false);
        setActivateSuccess(false);
        onActivated?.();
      }, 1500);
    } catch {
      setActivateError('Activation failed. Please try again.');
    }
    setActivating(false);
  };

  return (
    <>
      {/* Banner */}
      <div
        className={`flex items-center gap-3 px-4 py-2.5 text-sm border-b ${
          isExpired
            ? 'bg-red-500/10 border-red-500/20 text-red-300'
            : isUrgent
              ? 'bg-amber-500/10 border-amber-500/20 text-amber-300'
              : 'bg-violet-500/10 border-violet-500/20 text-violet-300'
        }`}
      >
        <Icon
          name={isExpired ? 'ExclamationCircleIcon' : 'ClockIcon'}
          size={15}
          className="flex-shrink-0"
        />
        <span className="flex-1 min-w-0">
          <span className="sm:hidden">Demo mode — contact us to unlock full access.</span>
          <span className="hidden sm:inline">
            {isExpired
              ? 'Your 7-day demo has expired. Activate a licence to continue using Diamond CRM.'
              : `You're currently in demo mode. Contact us to purchase a full license and unlock all features, premium support, and unrestricted access.`}
          </span>
        </span>
        <a
          href="/contact"
          className={`flex-shrink-0 text-xs font-semibold px-3 py-1 rounded-lg border transition-colors ${
            isExpired
              ? 'border-red-400/40 hover:bg-red-500/20'
              : 'border-violet-400/40 hover:bg-violet-500/20'
          }`}
        >
          Contact Us
        </a>
        {!isExpired && (
          <button
            onClick={handleDismiss}
            className="text-muted-foreground hover:text-foreground flex-shrink-0"
          >
            <Icon name="XMarkIcon" size={14} />
          </button>
        )}
      </div>

      {/* Activation Modal */}
      {showActivateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                  <Icon name="KeyIcon" size={14} className="text-white" />
                </div>
                <h3 className="text-sm font-semibold text-foreground">Activate Licence</h3>
              </div>
              <button
                onClick={() => {
                  setShowActivateModal(false);
                  setActivateError('');
                  setKeyInput('');
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="XMarkIcon" size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {activateSuccess ? (
                <div className="flex flex-col items-center gap-3 py-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Icon name="CheckCircleIcon" size={28} className="text-emerald-400" />
                  </div>
                  <p className="text-sm font-medium text-foreground">
                    Licence activated successfully!
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Your yearly licence is now active.
                  </p>
                </div>
              ) : (
                <>
                  <div className="bg-surface rounded-xl p-4 space-y-2">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="InformationCircleIcon" size={14} />
                      <span>Yearly licence includes 365 days of full access to all features.</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                      Licence Key
                    </label>
                    <input
                      type="text"
                      value={keyInput}
                      onChange={(e) => {
                        setKeyInput(e.target.value.toUpperCase());
                        setActivateError('');
                      }}
                      placeholder="DCM-XXXXX-XXXXX-XXXXX-XXXXX"
                      className="input-base w-full font-mono text-sm tracking-wider"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Enter the licence key provided by your administrator.
                    </p>
                  </div>
                  {activateError && (
                    <div className="flex items-center gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                      <Icon name="ExclamationCircleIcon" size={13} />
                      {activateError}
                    </div>
                  )}
                </>
              )}
            </div>

            {!activateSuccess && (
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
                <button
                  onClick={() => {
                    setShowActivateModal(false);
                    setActivateError('');
                    setKeyInput('');
                  }}
                  className="px-4 py-2 text-sm rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-surface transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleActivate}
                  disabled={activating || !keyInput.trim()}
                  className="px-4 py-2 text-sm rounded-lg gradient-indigo text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {activating ? 'Activating...' : 'Activate'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
